import { useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import api from "../lib/api";
import { toast } from "sonner";

const REASONS = [
    ["illegal_content", "Rechtswidriger Inhalt"],
    ["fraud_suspicion", "Betrugsverdacht"],
    ["counterfeit", "Produktfälschung / Markenrechtsverletzung"],
    ["stolen_vehicle", "Verdacht auf Diebesgut"],
    ["misleading", "Irreführende Angaben"],
    ["data_violation", "Datenschutz-/Persönlichkeitsrechtsverletzung"],
    ["other", "Sonstiges"],
];

export default function ReportListingDialog({ listingId }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("illegal_content");
    const [detail, setDetail] = useState("");
    const [email, setEmail] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async () => {
        if (detail.trim().length < 5) { toast.error("Bitte beschreiben Sie das Problem (min. 5 Zeichen)."); return; }
        setBusy(true);
        try {
            const { data } = await api.post(`/listings/${listingId}/report`, {
                reason, detail: detail.trim(), reporter_email: email.trim() || undefined,
            });
            toast.success(data.message || "Meldung eingegangen.");
            setOpen(false); setDetail(""); setEmail("");
        } catch {
            toast.error("Meldung konnte nicht gesendet werden.");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <button className="inline-flex items-center gap-1.5 text-xs text-gray-400 hover:text-red-600 transition-colors" data-testid="report-listing-btn">
                    <Flag className="w-3.5 h-3.5" /> Angebot melden
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md" data-testid="report-listing-dialog">
                <DialogHeader>
                    <DialogTitle>Angebot melden</DialogTitle>
                    <DialogDescription className="text-xs text-gray-500">Melden Sie rechtswidrige oder irreführende Inhalte. Wir prüfen jede Meldung (DSA Art. 16).</DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1">
                    <div>
                        <label className="text-xs font-bold text-gray-700">Grund</label>
                        <select value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1 w-full rounded-sm border border-gray-300 px-3 py-2 text-sm" data-testid="report-reason-select">
                            {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-700">Beschreibung</label>
                        <Textarea value={detail} onChange={(e) => setDetail(e.target.value)} rows={4} placeholder="Was ist an diesem Angebot problematisch?" className="mt-1" data-testid="report-detail-input" />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-gray-700">Ihre E-Mail (optional, für Rückfragen)</label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="mt-1" data-testid="report-email-input" />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={submit} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="report-submit-btn">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Flag className="w-4 h-4 mr-2" />} Meldung senden
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
